import React from 'react';
import { Form, Input, DatePicker, Switch, Button, Row, Col, Card, Divider, Space } from 'antd';
import { SaveOutlined, PrinterOutlined, FileTextOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const ClinicalNotes = () => {
  const [form] = Form.useForm();

  const handleSave = (values) => {
    console.log('Saving clinical notes:', values);
    // Implement save functionality
  };

  const handlePrint = () => {
    window.print();
  };

  const commonComplaints = [
    'Tooth pain',
    'Sensitivity to hot/cold',
    'Bleeding gums',
    'Bad breath',
    'Loose tooth',
    'Broken tooth',
    'Routine checkup'
  ];

  const commonFindings = [
    'Plaque buildup',
    'Tartar deposits',
    'Gingivitis',
    'Dental caries',
    'Tooth mobility',
    'Enamel wear',
    'Gum recession'
  ];

  const commonTreatments = [
    'Oral prophylaxis',
    'Composite restoration',
    'Root canal treatment',
    'Tooth extraction',
    'Crown placement',
    'Periodontal therapy'
  ];

  return (
    <div style={{ padding: '16px', maxWidth: '1000px', margin: '0 auto' }}>
      <Card title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileTextOutlined />
          Clinical Documentation
        </div>
      }>
        <Form 
          form={form}
          layout="vertical" 
          onFinish={handleSave}
          initialValues={{
            visitDate: null,
            followUpRequired: false
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="visitDate" label="Visit Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="appointmentType" label="Appointment Type">
                <Input placeholder="e.g., Emergency, Routine, Follow-up" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Patient Assessment</Divider>
          
          <Form.Item label="Chief Complaint">
            <Form.Item name="chiefComplaint" noStyle>
              <TextArea 
                rows={3} 
                placeholder="Patient's main concern or reason for visit (e.g., tooth pain, sensitivity, routine checkup)"
              />
            </Form.Item>
            <div style={{ marginTop: '8px' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>Common: </span>
              {commonComplaints.map(complaint => (
                <Button 
                  key={complaint}
                  size="small" 
                  type="link" 
                  style={{ padding: '0 4px', fontSize: '12px' }}
                  onClick={() => {
                    const currentValue = form.getFieldValue('chiefComplaint') || '';
                    const newValue = currentValue ? `${currentValue}, ${complaint}` : complaint;
                    form.setFieldsValue({ chiefComplaint: newValue });
                  }}
                >
                  {complaint}
                </Button>
              ))}
            </div>
          </Form.Item>
          
          <Form.Item label="Clinical Findings">
            <Form.Item name="clinicalFindings" noStyle>
              <TextArea 
                rows={4} 
                placeholder="Detailed clinical observations and findings (e.g., visible decay, gum inflammation, plaque buildup)"
              />
            </Form.Item>
            <div style={{ marginTop: '8px' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>Common: </span>
              {commonFindings.map(finding => (
                <Button 
                  key={finding}
                  size="small" 
                  type="link" 
                  style={{ padding: '0 4px', fontSize: '12px' }}
                  onClick={() => {
                    const currentValue = form.getFieldValue('clinicalFindings') || '';
                    const newValue = currentValue ? `${currentValue}, ${finding}` : finding;
                    form.setFieldsValue({ clinicalFindings: newValue });
                  }}
                >
                  {finding}
                </Button>
              ))}
            </div>
          </Form.Item>
          
          <Form.Item name="diagnosis" label="Diagnosis">
            <TextArea 
              rows={3} 
              placeholder="Clinical diagnosis based on examination (e.g., dental caries, gingivitis, periodontitis)"
            />
          </Form.Item>

          <Divider orientation="left">Treatment Planning</Divider>
          
          <Form.Item label="Treatment Plan">
            <Form.Item name="treatmentPlan" noStyle>
              <TextArea 
                rows={4} 
                placeholder="Proposed treatment plan and procedures (e.g., fillings, cleanings, extractions, follow-up schedule)"
              />
            </Form.Item>
            <div style={{ marginTop: '8px' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>Common: </span>
              {commonTreatments.map(treatment => (
                <Button 
                  key={treatment}
                  size="small" 
                  type="link" 
                  style={{ padding: '0 4px', fontSize: '12px' }}
                  onClick={() => {
                    const currentValue = form.getFieldValue('treatmentPlan') || '';
                    const newValue = currentValue ? `${currentValue}, ${treatment}` : treatment;
                    form.setFieldsValue({ treatmentPlan: newValue });
                  }}
                >
                  {treatment}
                </Button>
              ))}
            </div>
          </Form.Item>
          
          <Form.Item name="homeRecommendations" label="Home Care Recommendations">
            <TextArea 
              rows={3} 
              placeholder="Patient education and home care instructions (e.g., brushing technique, flossing, dietary recommendations)"
            />
          </Form.Item>

          <Divider orientation="left">Follow-up & Additional Notes</Divider>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="nextAppointment" label="Next Appointment">
                <DatePicker 
                  style={{ width: '100%' }} 
                  placeholder="Schedule follow-up"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="followUpRequired" label="Follow-up Required" valuePropName="checked">
                <Switch 
                  checkedChildren="Yes" 
                  unCheckedChildren="No" 
                  style={{ marginTop: '4px' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="additionalNotes" label="Additional Notes">
            <TextArea 
              rows={3} 
              placeholder="Any additional observations, patient concerns, or special instructions"
            />
          </Form.Item>

          <Form.Item name="medicationsPrescribed" label="Medications Prescribed">
            <TextArea 
              rows={2} 
              placeholder="List any medications prescribed with dosage and instructions"
            />
          </Form.Item>
          
          <Form.Item>
            <Space size="large">
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} size="large">
                Save Clinical Notes
              </Button>
              <Button icon={<PrinterOutlined />} size="large" onClick={handlePrint}>
                Print Notes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ClinicalNotes;